import {assert} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {
  DefaultValuesByColumnType,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {ProcessTask} from 'processes/__generated/ProcessTask';
import {Schema} from 'schemas/SchemaConstants';

import {ArchiveRecordGraphQL} from '../../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {ProcessTaskStatusLifecycleGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';
import {ProcessTaskStatus} from '../__generated/ProcessTaskStatus';
import {ProcessTableLocation} from '../ProcessConstants';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableMetadata,
} from './ProcessTaskStructuredTableAdapter';

/**
 * Creates a structured table from a raw Process object
 */

export interface CreateTaskTableTaskParams {
  id: string;
  humanId: string;
  fields: Array<SchemaTag>;
  archiveRecord: ArchiveRecordGraphQL | null;
  assignee?: {id: string; handle: string; name: string; avatarUrl: string | null} | null;
  status?: ProcessTaskStatus;
  scheduledOnDate?: string | null;
  executionOrigin?: null | {
    executionType: string;
    executionEntry: null | {
      id: string;
      name: string;
      editUrl: string;
    };
  };
  isPersisted?: boolean;
  studies: ProcessTask['studies'];
}

export interface CreateTaskTableSchemaWithStatusLifecycleParams extends Schema {
  id: string;
  name: string;
  statusLifecycle: ProcessTaskStatusLifecycleGraphQL;
}

export interface CreateTaskTableSchemaWithoutStatusLifecycleParams extends Schema {
  id: string;
  name: string;
  statusLifecycle?: null;
}

export type CreateTaskTableSchemaParams =
  | CreateTaskTableSchemaWithStatusLifecycleParams
  | CreateTaskTableSchemaWithoutStatusLifecycleParams;

interface CreateTaskTableParams {
  process: {
    id: string;
    name: string;
    processTaskSchema: CreateTaskTableSchemaParams;
    tasks: Array<CreateTaskTableTaskParams>;
  };
  tableLocation: ProcessTableLocation;
  selectorsById: SchemaFieldSelectorsDict;
  locked?: boolean;
  isInFlowchartNode?: boolean;
  defaultValuesByColumnType?: DefaultValuesByColumnType;
}

function makeTaskTableRows(
  table: Omit<ProcessTaskStructuredTable, 'cid'>,
  adapter: ProcessTaskStructuredTableAdapter,
  tasks: Array<CreateTaskTableTaskParams>,
  metadata: ProcessTaskStructuredTableMetadata
): Array<StructuredTableRow<ProcessTaskStructuredTableMetadata>> {
  if (tasks.length === 0) {
    return [{...adapter.getBlankRow({}, 0, table.columns), isFinalized: false}];
  }
  return tasks.map((task, taskIndex) => ({
    ...adapter.getBlankRow(metadata, taskIndex, table.columns),
    ...ProcessTaskStructuredTableAdapter.makeRowFromTask(task as ProcessTask, table.columns),
    isFinalized: task.isPersisted ?? true,
    metadata: adapter.getBlankRowMetadata(table.metadata),
    // We provide LUSVs because the structured table framework views a null LSUV as a newly inserted column
    lastSyncedUpstreamValues:
      ProcessTaskStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromTask(
        task as ProcessTask,
        table.columns
      ),
    lastSyncedArchivePurpose: task.archiveRecord?.purpose ?? null,
  }));
}

export type CreateTaskTableFromProcessTask = CreateTaskTableTaskParams & {
  schema: CreateTaskTableSchemaParams;
};

interface CreateTaskTableFromProcessTaskArgsBase {
  tasks: Array<CreateTaskTableFromProcessTask>;
  tableLocation: ProcessTableLocation;
  selectorsById: SchemaFieldSelectorsDict;
}
interface CreateTaskTableFromTaskArgsUnlocked extends CreateTaskTableFromProcessTaskArgsBase {
  locked: false;
}

interface CreateTaskTableFromProcessTaskArgsLocked extends CreateTaskTableFromProcessTaskArgsBase {
  locked: true;
  editableColumns?: Array<TableColumnType>;
}

type CreateTaskTableFromProcessTaskArgs =
  | CreateTaskTableFromTaskArgsUnlocked
  | CreateTaskTableFromProcessTaskArgsLocked;

export function createTaskTableFromTasks(
  args: CreateTaskTableFromProcessTaskArgs
): ProcessTaskStructuredTable {
  const {tasks, tableLocation, selectorsById} = args;
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  const metadata = {
    tableLocation,
  };
  assert(
    tasks.every((task) => task.schema.id === tasks[0].schema.id),
    'All tasks must have the same schema',
    {tasks}
  );
  const createProps = {
    name: tasks[0].schema.name,
    schemas: [
      {
        ...tasks[0].schema,
        columnGroup: null,
      },
    ],
    entryId: null,
    metadata,
    selectorsById,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  };

  const table = adapter.createTable(createProps);
  if (args.locked) {
    const editableColumns = args.editableColumns ?? [];
    table.columns = table.columns.map((col) => ({
      ...col,
      isReadOnly: !editableColumns.includes(col.columnType),
    }));
  }
  table.rows = makeTaskTableRows(table, adapter, tasks, metadata);
  return {...table, cid: `${tasks[0].schema.id}-tasks`};
}

export default function createTaskTable({
  process,
  tableLocation,
  locked,
  selectorsById,
  isInFlowchartNode,
  defaultValuesByColumnType,
}: CreateTaskTableParams): ProcessTaskStructuredTable {
  const adapter = ProcessTaskStructuredTableAdapter.getAdapter();
  const metadata = {
    tableLocation,
    processId: process.id,
    isInFlowchartNode,
  };
  const createProps = {
    name: process.processTaskSchema.name,
    schemas: [
      {
        ...process.processTaskSchema,
        columnGroup: null,
      },
    ],
    entryId: null,
    metadata,
    selectorsById,
    defaultValuesByColumnType,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  };

  const table = adapter.createTable(createProps);
  if (locked) {
    table.columns = table.columns.map((col) => ({...col, isReadOnly: true}));
  }
  table.rows = makeTaskTableRows(table, adapter, process.tasks, metadata);
  return {...table, cid: `${process.id}-tasks`};
}
